import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, Users, UserCog, Gift, HelpCircle, Settings as SettingsIcon, Phone, LogOut, Menu, X, Clapperboard, Send, Eraser, Archive, Bell, Trophy, Layers } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { SiteLogo } from "../../components/SiteLogo";
import api from "../../lib/api";

const MENU = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Genel Bakış", testid: "admin-nav-dashboard" },
  { to: "/admin/basvurular", icon: FileText, label: "Başvurular", testid: "admin-nav-applications", badge: true },
  { to: "/admin/yayincilar", icon: Users, label: "Yayıncı Yönetimi", testid: "admin-nav-publishers" },
  { to: "/admin/kullanicilar", icon: UserCog, label: "Kullanıcılar", testid: "admin-nav-users" },
  { to: "/admin/hediyeler", icon: Gift, label: "Hediye Görselleri", testid: "admin-nav-gifts" },
  { to: "/admin/videolar", icon: Clapperboard, label: "Video Arşiv", testid: "admin-nav-videos" },
  { to: "/admin/mesaj", icon: Send, label: "Toplu Mesaj", testid: "admin-nav-messages" },
  { to: "/admin/metin", icon: Eraser, label: "Metin Temizleme", testid: "admin-nav-cleaner" },
  { to: "/admin/kick", icon: Bell, label: "Kick Uyarıları", testid: "admin-nav-kick" },
  { to: "/admin/lig", icon: Trophy, label: "Lig Sıralamaları", testid: "admin-nav-league" },
  { to: "/admin/gruplar", icon: Layers, label: "İsim Gruplama", testid: "admin-nav-grouper" },
  { to: "/admin/arsiv", icon: Archive, label: "Yayıncı Arşivi", testid: "admin-nav-archive" },
  { to: "/admin/sss", icon: HelpCircle, label: "Sıkça Sorulan Sorular", testid: "admin-nav-faqs" },
  { to: "/admin/ayarlar", icon: SettingsIcon, label: "Site Ayarları", testid: "admin-nav-settings" },
  { to: "/admin/iletisim", icon: Phone, label: "İletişim Bilgileri", testid: "admin-nav-contact" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [newCount, setNewCount] = useState(0);

  useEffect(() => {
    if (user?.role !== "admin") return;
    const fetchCount = () => api.get("/admin/applications/new-count").then((r) => setNewCount(r.data.count)).catch(() => {});
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    window.addEventListener("applications-updated", fetchCount);
    return () => {
      clearInterval(interval);
      window.removeEventListener("applications-updated", fetchCount);
    };
  }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/panel" replace />;

  const handleLogout = async () => {
    await logout();
    navigate("/giris");
  };

  const sidebar = (
    <div className="flex flex-col h-full">
      <div className="flex items-center px-6 h-[72px] border-b border-white/[0.06]">
        <SiteLogo />
      </div>

      <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto" data-testid="admin-sidebar-nav">
        {MENU.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={item.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-xl text-[13px] font-medium transition-colors ${
                isActive
                  ? "bg-gradient-to-r from-[#0047FF]/25 to-[#00F0FF]/15 text-white border border-cyan-400/25"
                  : "text-[#A0ABC0] hover:text-white hover:bg-white/[0.04] border border-transparent"
              }`
            }
          >
            <item.icon size={17} strokeWidth={1.5} />
            <span className="flex-1">{item.label}</span>
            {item.badge && newCount > 0 && (
              <span data-testid="new-applications-badge" className="min-w-[20px] h-5 px-1.5 rounded-full bg-gradient-to-r from-[#0047FF] to-[#00F0FF] text-[10px] font-bold text-white flex items-center justify-center">
                {newCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="px-3 py-4 border-t border-white/[0.06]">
        <div className="px-4 py-2 mb-2">
          <p className="text-xs text-[#64748B]">Giriş yapan</p>
          <p className="text-xs font-semibold text-white truncate" data-testid="admin-user-email">{user?.email}</p>
        </div>
        <button
          onClick={handleLogout}
          data-testid="admin-logout-button"
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-[13px] font-medium text-[#A0ABC0] hover:text-red-300 hover:bg-red-500/10 transition-colors"
        >
          <LogOut size={17} strokeWidth={1.5} />
          Çıkış Yap
        </button>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-[#050505] flex">
      <aside className="hidden lg:block w-64 shrink-0 border-r border-white/[0.06] bg-[#070A12] fixed inset-y-0 left-0 z-40">
        {sidebar}
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-[#070A12] border-r border-white/[0.06]">{sidebar}</aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <div className="lg:hidden sticky top-0 z-30 h-16 bg-[#050505]/90 backdrop-blur-xl border-b border-white/[0.06] flex items-center justify-between px-5">
          <span className="font-display font-700 text-sm text-white">Hoom Agency</span>
          <button onClick={() => setOpen(!open)} data-testid="admin-mobile-menu-toggle" className="text-white p-2" aria-label="Menü">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        <main className="p-5 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
